import type { GetOneDiscount_content_discount } from '@/queries/types/GetOneDiscount';

interface ShowDiscountMessage {
  appliedDiscountCode: string;
  discount: GetOneDiscount_content_discount | null;
  discountIsSuccess: boolean;
  isAccessory: boolean;
  isOuraRing4Wizard: boolean;
}

interface GetValidDiscountStatus {
  hasValidGen3DiscountForGen3Wiz: boolean | null;
  hasValidGen4DiscountForGen4Wiz: boolean | null;
  hasValidAllRingsDiscount: boolean | null;
}

interface DiscountStatusWithPayload extends GetValidDiscountStatus {
  hasInvalidDiscount: boolean | null;
  hasLimitReached: boolean | null;
}

interface DiscountStatus extends DiscountStatusWithPayload {
  noDiscountFound: boolean;
}

const _getIsGen3Discount = (
  discount: GetOneDiscount_content_discount | null,
): boolean => {
  const ringCategories = discount?.priceRules?.[0]?.categories;
  return Boolean(
    ringCategories &&
      ringCategories.length > 0 &&
      !ringCategories.includes('rings') &&
      !ringCategories.includes('gen4-rings'),
  );
};

const _getIsGen3AndGen4Discount = (
  discount: GetOneDiscount_content_discount | null,
): boolean => {
  const ringCategories = discount?.priceRules?.[0]?.categories;
  const heritageAndGen4Rings = Boolean(
    ringCategories?.includes('gen4-rings') &&
      ringCategories.includes('heritage-rings'),
  );
  const horizonAndGen4Rings = Boolean(
    ringCategories?.includes('gen4-rings') &&
      ringCategories.includes('horizon-rings'),
  );
  const allRings = Boolean(ringCategories?.includes('rings'));

  return heritageAndGen4Rings || horizonAndGen4Rings || allRings;
};

const _getValidDiscountStatus = ({
  discount,
  discountIsSuccess,
  isOuraRing4Wizard,
}: Omit<
  ShowDiscountMessage,
  'appliedDiscountCode' | 'isAccessory'
>): GetValidDiscountStatus => {
  const isGen3Discount = _getIsGen3Discount(discount);
  const isAllRingsDiscount = _getIsGen3AndGen4Discount(discount);
  const isSuccessWithPayload = discountIsSuccess && discount;
  // If the discount is valid for Gen3 wizard, show the discount message on Gen3 only
  const hasValidGen3DiscountForGen3Wiz =
    isSuccessWithPayload && !isOuraRing4Wizard && isGen3Discount;
  // If the discount is valid for Gen4 wizard, show the discount message on Gen4 only
  const hasValidGen4DiscountForGen4Wiz =
    isSuccessWithPayload && isOuraRing4Wizard && !isGen3Discount;
  // If the discount is valid for all rings, show the discount message on all rings
  const hasValidAllRingsDiscount = isSuccessWithPayload && isAllRingsDiscount;

  return {
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  };
};

const _getIsDiscountWithPayload = ({
  discount,
  discountIsSuccess,
  isOuraRing4Wizard,
}: Omit<
  ShowDiscountMessage,
  'appliedDiscountCode' | 'isAccessory'
>): DiscountStatusWithPayload => {
  const isSuccessWithPayload = discountIsSuccess && discount;
  // If the discount is invalid, show the discount message
  const hasInvalidDiscount = isSuccessWithPayload && !discount.valid;
  // If the discount usage limit is reached, show the discount message
  const hasLimitReached =
    isSuccessWithPayload &&
    discount.usageCount === discount.usageLimit &&
    discount.valid;
  const {
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  } = _getValidDiscountStatus({
    discount,
    discountIsSuccess,
    isOuraRing4Wizard,
  });

  return {
    hasInvalidDiscount,
    hasLimitReached,
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  };
};

const _getDiscountStatus = ({
  appliedDiscountCode,
  discount,
  discountIsSuccess,
  isOuraRing4Wizard,
}: Omit<ShowDiscountMessage, 'isAccessory'>): DiscountStatus => {
  // If no discount is found, show the discount message
  const noDiscountFound =
    discountIsSuccess && !discount && appliedDiscountCode.length > 0;
  const {
    hasInvalidDiscount,
    hasLimitReached,
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  } = _getIsDiscountWithPayload({
    discount,
    discountIsSuccess,
    isOuraRing4Wizard,
  });
  return {
    noDiscountFound,
    hasInvalidDiscount,
    hasLimitReached,
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  };
};

const showDiscountMessage = ({
  appliedDiscountCode,
  discount,
  discountIsSuccess,
  isAccessory,
  isOuraRing4Wizard,
}: ShowDiscountMessage): boolean => {
  const {
    noDiscountFound,
    hasInvalidDiscount,
    hasLimitReached,
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  } = _getDiscountStatus({
    appliedDiscountCode,
    discount,
    discountIsSuccess,
    isOuraRing4Wizard,
  });
  switch (true) {
    case isAccessory:
      return false;
    case hasValidGen3DiscountForGen3Wiz:
      return true;
    case hasValidGen4DiscountForGen4Wiz:
      return true;
    case hasValidAllRingsDiscount:
      return true;
    case noDiscountFound:
      return true;
    case hasInvalidDiscount:
      return true;
    case hasLimitReached:
      return true;
    default:
      return false;
  }
};

export default showDiscountMessage;
